import classNames from 'classnames';
import { formatDistanceStrict } from 'date-fns';

import { useAuthContext } from '/src/hooks';

import { getVisitorDisplayName } from '/src/utils/visitor';
import { escapeRegexSpecialChars } from '/src/utils/regex';
import { useFilter } from '../live-filter-context';
import { getLiveEvent } from '../live-functions';
import { useRelativeTimeDate } from '../relative-time-context';
import { getEventCardSearchText } from '../search-text';
import { LiveEventIcon } from './LiveEventIcon';

import { LIVE_EVENT_NAMES, BASIC_VISITOR } from '../constants';

export function EventCard({ liveEvent, visitEvents, identifyEvents, pageCounts, pauseOnHover, pause }) {
  const { filter } = useFilter();
  const { sessionInfo } = useAuthContext();
  const currentDate = useRelativeTimeDate();

  const { name, CardComponent, tooltip } = getLiveEvent(
    liveEvent.event_type,
    liveEvent.data?.attributes?.sub_event_type,
  );

  let iconSubType;
  if (name === LIVE_EVENT_NAMES.CLICK) {
    if (liveEvent.data?.attributes?.dead) iconSubType = 'dead';
    if (liveEvent.data?.attributes?.rage) iconSubType = 'rage';
  }

  const displayedDate = formatDistanceStrict(new Date(liveEvent.timestamp), currentDate, { addSuffix: true });
  const searchText = getEventCardSearchText({
    liveEvent,
    identifyEvents,
    visitEvents,
    isImpersonated: sessionInfo?.isImpersonated ?? false,
  });
  const doesNotMatchSearch = filter.search
    ? !searchText.match(new RegExp(escapeRegexSpecialChars(filter.search), 'i'))
    : false;

  if (
    !name ||
    !filter.events.includes(name) ||
    (filter.person &&
      ((liveEvent.data?.visitor_id && liveEvent.data?.visitor_id !== filter.person) ||
        (!liveEvent.data?.visitor_id && filter.person !== BASIC_VISITOR))) ||
    (filter.page &&
      pageCounts[filter.page] &&
      !pageCounts[filter.page].visitIds.includes(liveEvent.data?.attributes?.visit_id) &&
      !pageCounts[filter.page].visitIds.includes(liveEvent.data?.event_id))
  )
    return null;
  return (
    <div
      className={classNames(
        'text-body-2 relative mb-3.75 min-w-max rounded-[10px] border border-mystic-500 bg-white py-3.75 pl-3.75 pr-5 hover:shadow',
        {
          'hidden ': doesNotMatchSearch,
        },
      )}
      onPointerEnter={() => {
        pauseOnHover && pause?.();
      }}
    >
      <LiveEventIcon name={name} subType={iconSubType} tooltip={tooltip} />
      <div className="flex w-full items-start justify-between">
        {CardComponent && (
          <CardComponent liveEvent={liveEvent} visitEvents={visitEvents} identifyEvents={identifyEvents} />
        )}
        <div className="text-body-5 ml-3 w-[75px] shrink-0 text-center leading-tight whitespace-normal">
          {displayedDate}
        </div>
      </div>
    </div>
  );
}

export function Identity({ id, identifyEvents }) {
  const { setPersonFilter, filter } = useFilter();
  const visitorId = id;
  const identifier = identifyEvents[visitorId];

  const displayName = getVisitorDisplayName(identifier, visitorId);

  return (
    <button
      type="button"
      onClick={() => {
        if (filter.person === visitorId) {
          setPersonFilter(undefined);
        } else {
          setPersonFilter(visitorId);
        }
      }}
      className={classNames('mr-1.25 shrink-0 font-semibold text-dodger-blue-500', {
        'w-32 truncate': !identifier,
      })}
    >
      {displayName}
    </button>
  );
}
